import React, { useCallback, useEffect, useMemo, useState } from "react";
import Calendar, {
  toBerlinISODate,
  startOfWeekMondayBerlin,
  weekDaysFromBerlin,
  addCalendarDays,
} from "components/Calendar";
import api from "utils/api";
import TeamScheduleGrid from "scenes/team/TeamScheduleGrid";
import TeamBookingDetailDialog from "scenes/team/TeamBookingDetailDialog";
import { useTeamLocale } from "scenes/team/TeamLocaleContext";
import { translateServiceLabel } from "scenes/team/formatters";

const VIEW_MODES = ["day", "week", "month"];

const EmployeeCalendarTab = ({ embedded = false }) => {
  const { t, dateLocale, locale } = useTeamLocale();
  const today = new Date();
  const [viewMode, setViewMode] = useState("month");
  const [anchorDate, setAnchorDate] = useState(today);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const anchorKey = toBerlinISODate(anchorDate);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get("/employee/calendar", {
        params: {
          view: viewMode,
          date: anchorKey,
          year: viewYear,
          month: viewMonth + 1,
        },
      });
      setData(res);
    } catch (err) {
      setError(err?.response?.data?.message || "Kalender konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [viewMode, anchorKey, viewYear, viewMonth]);

  useEffect(() => {
    load();
  }, [load]);

  const weekDays = useMemo(() => weekDaysFromBerlin(anchorDate), [anchorDate]);
  const selectedKey = toBerlinISODate(anchorDate);
  const daysMap = useMemo(() => data?.days ?? {}, [data?.days]);
  const daysWithBookings = useMemo(() => Object.keys(daysMap).length, [daysMap]);

  const minDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
  const maxDate = new Date(today.getFullYear(), today.getMonth() + 4, 0);

  const calendarLabels = useMemo(
    () => ({
      prev: t("calendar.prevMonth"),
      next: t("calendar.nextMonth"),
      closedDay: t("calendar.closedDay"),
      appointments: (count) => t("calendar.appointments", { count }),
    }),
    [t]
  );

  const setView = (mode) => {
    if (mode === "week") {
      const monday = startOfWeekMondayBerlin(anchorDate);
      setAnchorDate(monday);
      setViewYear(monday.getFullYear());
      setViewMonth(monday.getMonth());
    }
    setViewMode(mode);
  };

  const shiftAnchor = (deltaDays) => {
    const next = addCalendarDays(anchorDate, deltaDays);
    setAnchorDate(next);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  const selectDay = (day) => {
    setAnchorDate(day);
    setViewYear(day.getFullYear());
    setViewMonth(day.getMonth());
  };

  const navLabel =
    viewMode === "day"
      ? anchorDate.toLocaleDateString(dateLocale, {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "Europe/Berlin",
        })
      : viewMode === "week"
        ? t("calendar.weekRange", {
            from: weekDays[0].toLocaleDateString(dateLocale, {
              day: "numeric",
              month: "short",
              timeZone: "Europe/Berlin",
            }),
            to: weekDays[6].toLocaleDateString(dateLocale, {
              day: "numeric",
              month: "short",
              year: "numeric",
              timeZone: "Europe/Berlin",
            }),
          })
        : anchorDate.toLocaleDateString(dateLocale, {
            month: "long",
            year: "numeric",
            timeZone: "Europe/Berlin",
          });

  const navStep = viewMode === "day" ? 1 : viewMode === "week" ? 7 : 0;

  const layoutClass =
    viewMode === "week" || viewMode === "day"
      ? "team-calendar-layout--schedule"
      : "team-calendar-layout";

  const monthDayBookings = daysMap[selectedKey] || [];

  return (
    <section
      className={
        embedded ? "employee-panel employee-panel--calendar" : "team-calendar employee-calendar"
      }
    >
      {embedded ? (
        <h2>Terminkalender</h2>
      ) : (
        <header className="employee-calendar-header">
          <h2 className="employee-calendar-title">Terminkalender</h2>
        </header>
      )}
      {error && <div className="auth-alert is-error">{error}</div>}

      <div className="team-calendar-view-tabs" role="tablist" aria-label="Kalenderansicht">
        {VIEW_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            role="tab"
            aria-selected={viewMode === mode}
            className={viewMode === mode ? "is-active" : ""}
            onClick={() => setView(mode)}
          >
            {t(`calendar.view.${mode}`)}
          </button>
        ))}
      </div>

      {viewMode !== "month" && (
        <div className="team-calendar-range-nav">
          <button type="button" onClick={() => shiftAnchor(-navStep)}>
            {t("common.back")}
          </button>
          <span>{navLabel}</span>
          <button type="button" onClick={() => shiftAnchor(navStep)}>
            {t("common.next")}
          </button>
        </div>
      )}

      <div className={layoutClass}>
        {viewMode === "week" && (
          <TeamScheduleGrid
            days={weekDays}
            daysMap={daysMap}
            scheduleBrand="dr-hoffmann"
            dateLocale={dateLocale}
            t={t}
            selectedKey={selectedKey}
            today={today}
            onSelectDay={selectDay}
            onBookingSelect={setSelectedBooking}
            loading={loading}
          />
        )}

        {viewMode === "day" && (
          <TeamScheduleGrid
            days={[anchorDate]}
            daysMap={daysMap}
            scheduleBrand="dr-hoffmann"
            dateLocale={dateLocale}
            t={t}
            selectedKey={selectedKey}
            today={today}
            onBookingSelect={setSelectedBooking}
            loading={loading}
          />
        )}

        {viewMode === "month" && (
          <div className="team-calendar-picker">
            <Calendar
              selectedDate={anchorDate}
              markedDays={daysMap}
              dateLocale={dateLocale}
              labels={calendarLabels}
              weekdaysOnly={false}
              onMonthChange={(year, month) => {
                setViewYear(year);
                setViewMonth(month);
              }}
              onSelect={(d) => {
                setAnchorDate(d);
                setViewYear(d.getFullYear());
                setViewMonth(d.getMonth());
              }}
              minDate={minDate}
              maxDate={maxDate}
            />
            <p className="team-calendar-legend" aria-hidden="true">
              <span className="team-calendar-legend-swatch has-bookings" />
              {t("calendar.legend")}
            </p>
            {loading && (
              <p className="team-muted" style={{ marginTop: "0.75rem" }}>
                {t("common.loading")}
              </p>
            )}
            {!loading && daysWithBookings > 0 && (
              <p className="team-muted" style={{ marginTop: "0.75rem", fontSize: "0.8rem" }}>
                {t("calendar.daysWithBookings", { count: daysWithBookings })}
              </p>
            )}
          </div>
        )}

        {viewMode === "month" && (
          <div className="team-calendar-day">
            <h3>
              {anchorDate.toLocaleDateString(dateLocale, {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
                timeZone: "Europe/Berlin",
              })}
            </h3>
            {loading ? (
              <p className="team-muted">{t("common.loading")}</p>
            ) : monthDayBookings.length === 0 ? (
              <div className="team-empty">{t("calendar.noAppointments")}</div>
            ) : (
              <ul className="team-day-list">
                {monthDayBookings.map((b) => (
                  <li key={b._id}>
                    <button
                      type="button"
                      className="team-day-item team-day-item--clickable"
                      onClick={() => setSelectedBooking(b)}
                    >
                      <div className="team-day-time">
                        {new Date(b.start).toLocaleTimeString(dateLocale, {
                          hour: "2-digit",
                          minute: "2-digit",
                          timeZone: "Europe/Berlin",
                        })}
                        <span>
                          {b.durationMinutes} {t("common.minutes")}
                        </span>
                      </div>
                      <div>
                        <strong>{translateServiceLabel(t, b)}</strong>
                        <p className="team-muted">
                          {b.customer?.firstName} {b.customer?.lastName}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <TeamBookingDetailDialog
        open={Boolean(selectedBooking)}
        booking={selectedBooking}
        dateLocale={dateLocale}
        locale={locale}
        t={t}
        showSource={false}
        showBrand={false}
        onClose={() => setSelectedBooking(null)}
      />
    </section>
  );
};

export default EmployeeCalendarTab;
